import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/shared/utils/cn';

const badgeVariants = cva(
  'inline-flex items-center justify-center w-fit whitespace-nowrap shrink-0 font-semibold [&>svg]:pointer-events-none transition-[color,box-shadow] overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
  {
    variants: {
      variant: {
        default:
          'border-transparent bg-primary text-primary-foreground rounded-md px-2 py-0.5 text-xs [&>svg]:size-3 gap-1 [a&]:hover:bg-primary/90',
        secondary:
          'border-2 border-cold-700 bg-cold-900 text-cold-200 rounded-md px-2 py-0.5 text-xs [&>svg]:size-3 gap-1 [a&]:hover:bg-secondary/90',
        blur: 'border-2 border-white/20 bg-white/5 text-cold-200 backdrop-blur-sm rounded-md px-2 py-0.5 text-xs [&>svg]:size-3 gap-1 [a&]:hover:bg-white/10',
        outline:
          'border border-border bg-transparent text-foreground rounded-md px-2 py-0.5 text-xs [&>svg]:size-3 gap-1 [a&]:hover:bg-accent [a&]:hover:text-accent-foreground',
        destructive:
          'border-transparent bg-destructive text-white rounded-md px-2 py-0.5 text-xs [&>svg]:size-3 gap-1 [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        display:
          'border-2 border-cold-700 bg-cold-950 text-cold-300 rounded-lg px-3 py-1.5 text-base leading-6 [&>svg]:size-4 gap-2 [a&]:hover:bg-secondary/90',
        notification:
          'border-transparent bg-pink-500 text-cold-100 rounded-full px-2 py-0.5 text-xs [&>svg]:size-3 gap-1',
        prominent:
          'border-2 border-cold-700 bg-positive-700 text-white rounded-md px-3 py-0.5 text-xs [&>svg]:size-3 gap-2',
        glass:
          'border-2 border-white/20 bg-black/40 text-cold-200 rounded-lg px-3 py-0.5 text-xs [&>svg]:size-4 gap-2',
      },
      number: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      // Number variants (circular) for default, secondary, outline, destructive
      // Only override shape, size, and spacing - colors come from base variant
      {
        variant: 'default',
        number: true,
        class: 'rounded-full min-w-5 h-5 px-1 py-0.5 gap-0',
      },
      {
        variant: 'secondary',
        number: true,
        class: 'rounded-full border-0 min-w-5 h-5 px-1 py-0.5 gap-0',
      },
      {
        variant: 'blur',
        number: true,
        class: 'rounded-full border-0 min-w-5 h-5 px-1 py-0.5 gap-0',
      },
      {
        variant: 'outline',
        number: true,
        class: 'rounded-full min-w-5 h-5 px-1 py-0.5 gap-0',
      },
      {
        variant: 'destructive',
        number: true,
        class: 'rounded-full min-w-5 h-5 px-1 py-0.5 gap-0',
      },
      {
        variant: 'notification',
        number: true,
        class: 'rounded-full min-w-5 h-5 px-1 py-0.5 gap-0',
      },
    ],
    defaultVariants: {
      variant: 'default',
      number: false,
    },
  },
);

export interface BadgeProps
  extends React.ComponentProps<'span'>, VariantProps<typeof badgeVariants> {
  asChild?: boolean;
}

function Badge({
  className,
  variant,
  number,
  asChild = false,
  ...props
}: BadgeProps) {
  const Comp = asChild ? Slot : 'span';

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant, number }), className)}
      {...props}
    />
  );
}

export { Badge, badgeVariants };
